import type { User as FirebaseUser } from 'firebase/auth';
import { auth } from './firebase';
import type { AccessTransport } from '../components/access/AccessManager';
import type { ServiceKey, ServiceGrant } from '../components/access/policy';

const endpoint = import.meta.env.VITE_BFGC_ACCESS_API_URL || 'https://www.bfgc.kr/api/access';
const prepared = import.meta.env.VITE_FIREBASE_PROJECT_ID === 'bfgc-online-church';
/** 인증 토큰은 HTTPS 요청 헤더에만 전달하며 출력하거나 저장하지 않습니다. */
export const accessRequest: AccessTransport = async (path, init) => {
  const user = auth?.currentUser;
  if (!user) throw new Error('공통 교회 계정으로 로그인해 주세요.');
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${await user.getIdToken()}`);
  return fetch(`${endpoint.replace(/\/$/, '')}/${path}`, { ...init, headers, credentials: 'omit', cache: 'no-store' });
};
export async function prepareCentralAccess(user: FirebaseUser) {
  if (!prepared) return false;
  const account = await readCentralMe(user, 10000);
  // 중앙 권한 모드가 켜진 경우에만 중앙 권한이 기존 행정 권한을 대체합니다.
  return account.enabled;
}
export interface CentralState { ready?: boolean; enabled: boolean; active: boolean; grant: ServiceGrant | null; error?: boolean }
export function watchCentralAccess(user: FirebaseUser, service: ServiceKey, onChange: (state: CentralState) => void) {
  if (!prepared) { onChange({ ready: true, enabled: false, active: false, grant: null }); return () => {}; }
  let active = true;
  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
    if (active) onChange({ ready: true, enabled: true, active: false, grant: null, error: true });
  }, 12000);
  void readCentralMe(user, 12000, controller.signal).then((account) => {
    if (!active) return;
    const grant = account.grants[service] || null;
    onChange({ ready: true, enabled: account.enabled, active: account.enabled && grant?.active === true && grant.role !== 'none', grant });
  }).catch(() => {
    if (active) onChange({ ready: true, enabled: true, active: false, grant: null, error: true });
  }).finally(() => clearTimeout(timeout));
  return () => { active = false; clearTimeout(timeout); controller.abort(); };
}

type CentralMe = { enabled: boolean; grants: Partial<Record<ServiceKey, ServiceGrant>> };
async function readCentralMe(user: FirebaseUser, ms: number, signal?: AbortSignal): Promise<CentralMe> {
  const controller = signal ? undefined : new AbortController();
  const timeout = controller ? setTimeout(() => controller.abort(), ms) : undefined;
  try {
    const response = await accessRequest('me', { signal: signal || controller?.signal });
    if (!response.ok) throw new Error('공통 계정 연결을 확인하지 못했습니다. 다시 로그인해 주세요.');
    const data = await response.json() as Partial<CentralMe>;
    if (typeof data.enabled !== 'boolean' || !data.grants || typeof data.grants !== 'object') throw new Error('공통 권한 응답을 읽지 못했습니다.');
    if (auth.currentUser?.uid !== user.uid) throw new Error('로그인 계정이 변경되었습니다.');
    return data as CentralMe;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}
